import {
  addMinutes,
  differenceInCalendarDays,
  endOfDay,
  max,
  min,
  parse,
  parseISO,
  startOfDay,
} from "date-fns";

export interface AppointmentSpanInput {
  scheduled_date: string;
  duration_minutes?: number | null;
  is_all_day?: boolean;
}

/** Local calendar start — all-day uses date part only (avoids UTC shift). */
export function getAppointmentStart(appt: AppointmentSpanInput): Date {
  if (appt.is_all_day) {
    const datePart = appt.scheduled_date.slice(0, 10);
    return parse(datePart, "yyyy-MM-dd", new Date());
  }
  return parseISO(appt.scheduled_date);
}

export function getAppointmentEnd(appt: AppointmentSpanInput): Date {
  const start = getAppointmentStart(appt);
  const duration = appt.duration_minutes ?? (appt.is_all_day ? 1440 : 60);
  return addMinutes(start, duration);
}

/** True if the appointment intersects the given calendar day (local). */
export function appointmentOverlapsDay(appt: AppointmentSpanInput, day: Date): boolean {
  const dayStart = startOfDay(day);
  const dayEnd = endOfDay(day);
  const eventStart = getAppointmentStart(appt);
  const eventEnd = getAppointmentEnd(appt);
  return eventStart <= dayEnd && eventEnd > dayStart;
}

export interface AllDayWeekSpan {
  startCol: number;
  span: number;
}

/**
 * Column span for an all-day event within a week row (0-based columns).
 * Returns null if the event does not overlap the week.
 */
export function getAllDaySpanInWeek(
  appt: AppointmentSpanInput,
  weekDays: Date[],
): AllDayWeekSpan | null {
  if (weekDays.length === 0) return null;

  const eventStart = startOfDay(getAppointmentStart(appt));
  const eventEnd = getAppointmentEnd(appt);
  const weekStart = startOfDay(weekDays[0]);
  const weekEnd = endOfDay(weekDays[weekDays.length - 1]);

  if (eventEnd <= weekStart || eventStart > weekEnd) return null;

  let startCol = -1;
  let endCol = -1;

  weekDays.forEach((day, index) => {
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);
    if (eventStart <= dayEnd && eventEnd > dayStart) {
      if (startCol === -1) startCol = index;
      endCol = index;
    }
  });

  if (startCol === -1) return null;
  return { startCol, span: endCol - startCol + 1 };
}

/** Calendar days spanned (for labels). */
export function getAllDayCalendarDayCount(appt: AppointmentSpanInput): number {
  const start = startOfDay(getAppointmentStart(appt));
  const end = getAppointmentEnd(appt);
  return Math.max(1, differenceInCalendarDays(end, start));
}

export interface TimedEventSpan {
  id: string;
  start: Date;
  end: Date;
}

export interface TimedEventColumnLayout {
  column: number;
  totalColumns: number;
  leftPercent: number;
  widthPercent: number;
}

const TIMED_EVENT_PX_PER_MINUTE = 1;

export function timedEventsOverlap(a: TimedEventSpan, b: TimedEventSpan): boolean {
  return a.start < b.end && b.start < a.end;
}

export function getTimedEventVerticalPosition(
  start: Date,
  end: Date,
): { topPx: number; heightPx: number } {
  const startMinutes = start.getHours() * 60 + start.getMinutes();
  const durationMinutes = Math.max(0, (end.getTime() - start.getTime()) / 60_000);
  return {
    topPx: startMinutes * TIMED_EVENT_PX_PER_MINUTE,
    heightPx: Math.max(durationMinutes * TIMED_EVENT_PX_PER_MINUTE, 18),
  };
}

function buildOverlapGroups(events: TimedEventSpan[]): TimedEventSpan[][] {
  if (events.length === 0) return [];

  const parent = new Map<string, string>();
  for (const event of events) parent.set(event.id, event.id);

  const find = (id: string): string => {
    let root = id;
    while (parent.get(root) !== root) {
      root = parent.get(root)!;
    }
    return root;
  };

  const union = (a: string, b: string) => {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) parent.set(rootA, rootB);
  };

  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      if (timedEventsOverlap(events[i], events[j])) {
        union(events[i].id, events[j].id);
      }
    }
  }

  const groups = new Map<string, TimedEventSpan[]>();
  for (const event of events) {
    const root = find(event.id);
    if (!groups.has(root)) groups.set(root, []);
    groups.get(root)!.push(event);
  }

  return [...groups.values()];
}

const OVERLAP_RIGHT_INSET_PX = 6;

function assignColumnsForEvents(
  events: TimedEventSpan[],
): Map<string, TimedEventColumnLayout> {
  const layouts = new Map<string, TimedEventColumnLayout>();
  if (events.length === 0) return layouts;

  const sorted = [...events].sort((a, b) => {
    const startDiff = a.start.getTime() - b.start.getTime();
    if (startDiff !== 0) return startDiff;
    return b.end.getTime() - a.end.getTime();
  });

  const columnEnds: Date[] = [];
  const eventColumns = new Map<string, number>();

  for (const event of sorted) {
    let column = columnEnds.findIndex((columnEnd) => columnEnd <= event.start);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(event.end);
    } else {
      columnEnds[column] = event.end;
    }
    eventColumns.set(event.id, column);
  }

  const totalColumns = columnEnds.length;

  for (const event of sorted) {
    const column = eventColumns.get(event.id)!;
    layouts.set(event.id, {
      column,
      totalColumns,
      leftPercent: (column / totalColumns) * 100,
      widthPercent: ((totalColumns - column) / totalColumns) * 100,
    });
  }

  return layouts;
}

/** Cascading overlap layout for concurrent timed events (Google Calendar day/week). */
export function layoutOverlappingTimedEvents(
  events: TimedEventSpan[],
): Map<string, TimedEventColumnLayout> {
  const layouts = new Map<string, TimedEventColumnLayout>();
  for (const group of buildOverlapGroups(events)) {
    const groupLayouts = assignColumnsForEvents(group);
    for (const [id, layout] of groupLayouts) {
      layouts.set(id, layout);
    }
  }
  return layouts;
}

export interface TimedEventRenderPiece {
  key: string;
  eventId: string;
  start: Date;
  end: Date;
  column: number;
  totalColumns: number;
}

/** One render piece per event (full height). Overlap layout uses the concurrent window. */
export function buildTimedEventRenderPieces(events: TimedEventSpan[]): TimedEventRenderPiece[] {
  const uniqueEvents = [...new Map(events.map((event) => [event.id, event])).values()];
  const layouts = layoutOverlappingTimedEvents(uniqueEvents);

  return uniqueEvents.map((event) => {
    const layout = layouts.get(event.id);
    return {
      key: event.id,
      eventId: event.id,
      start: event.start,
      end: event.end,
      column: layout?.column ?? 0,
      totalColumns: layout?.totalColumns ?? 1,
    };
  });
}

export function getTimedEventPositionStyle(
  start: Date,
  end: Date,
  columnLayout?: Pick<TimedEventColumnLayout, "column" | "totalColumns" | "leftPercent" | "widthPercent">,
): {
  top: string;
  height: string;
  left: string;
  width: string;
  column: number;
  totalColumns: number;
} {
  const { topPx, heightPx } = getTimedEventVerticalPosition(start, end);

  if (!columnLayout || columnLayout.totalColumns <= 1) {
    return {
      top: `${topPx}px`,
      height: `${heightPx}px`,
      left: "4px",
      width: "calc(100% - 8px)",
      column: 0,
      totalColumns: 1,
    };
  }

  const layersAbove = columnLayout.totalColumns - 1 - columnLayout.column;
  const rightInsetPx = layersAbove * OVERLAP_RIGHT_INSET_PX;
  const width =
    rightInsetPx > 0
      ? `calc(${columnLayout.widthPercent}% - ${rightInsetPx}px)`
      : `${columnLayout.widthPercent}%`;

  return {
    top: `${topPx}px`,
    height: `${heightPx}px`,
    left: `${columnLayout.leftPercent}%`,
    width,
    column: columnLayout.column,
    totalColumns: columnLayout.totalColumns,
  };
}
